import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { CoreService, HsApiService } from '../../../services'
import { ProductDetailService } from '../../../services/product-detail.service'

export type HsProductAttachment = {
  id: number
  kind: string
  name: string
  mimeType: string
  extension: string
  size: number | null
  country: string
  channel: string
  url: string
  updated: string
}

export type HsProductAttachmentGroup = {
  kind: string
  files: HsProductAttachment[]
}

@Component({
  selector: 'product-files',
  templateUrl: './product-files.component.html',
  styleUrls: ['./product-files.component.scss'],
})
export class ProductFilesComponent implements OnInit {
  loading = false
  attachmentGroups: HsProductAttachmentGroup[] = []

  constructor(
    private readonly core: CoreService,
    private readonly hsApi: HsApiService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    public productDetailService: ProductDetailService,
  ) {}

  async ngOnInit() {
    const product = this.productDetailService.product
    if (!product?.externalId) {
      if (product?.id) {
        await this.router.navigate(['/product', product.id])
      }
      return
    }
    await this.loadAttachments(product.externalId)
  }

  async loadAttachments(externalId: string) {
    this.loading = true
    try {
      // attachments are listed even when the HS template is currently not sellable
      const template = await this.hsApi.getHsTemplate(externalId, false)
      this.attachmentGroups = this.normalizeAttachments(template?.attachments)
    } catch (e) {
      console.log(e)
      this.attachmentGroups = []
    } finally {
      this.loading = false
    }
  }

  getKindLabel(kind: string): string {
    const key = `product.files.kind.${kind}`
    const translated = this.translateService.instant(key)
    return translated === key ? kind : translated
  }

  getFileIcon(file: HsProductAttachment): string {
    const mime = file?.mimeType || ''
    const ext = (file?.extension || '').toLowerCase()
    if (mime.includes('pdf') || ext === 'pdf') {
      return 'far fa-file-pdf'
    }
    if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return 'far fa-file-image'
    }
    return 'far fa-file'
  }

  showDocument(url: string) {
    if (!url) {
      return
    }
    this.core.services.download.downloadDocumentAndShowInNewWindow(url, false)
  }

  private normalizeAttachments(attachments: any): HsProductAttachmentGroup[] {
    if (!attachments) {
      return []
    }
    if (Array.isArray(attachments)) {
      return attachments.length ? [{ kind: 'general', files: attachments }] : []
    }
    return Object.keys(attachments)
      .filter(kind => Array.isArray(attachments[kind]) && attachments[kind].length > 0)
      .map(kind => ({ kind, files: attachments[kind] }))
  }
}
